import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './GoalModal.css';
import { assessmentService, goalService } from '../services/api';
import { Modal } from 'react-responsive-modal';

// Main GoalModal component
const GoalModal = ({ isOpen, onClose, onGoalCreated }) => {
  const [goalDescription, setGoalDescription] = useState('');
  const [skillLevel, setSkillLevel] = useState('Beginner');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [previewImage, setPreviewImage] = useState(null);
  
  // Assessment state
  const [assessmentId, setAssessmentId] = useState(null);
  const [assessmentQuestions, setAssessmentQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [showQuestions, setShowQuestions] = useState(false);
  const [isAssessmentComplete, setIsAssessmentComplete] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [userAnswers, setUserAnswers] = useState({});
  const [isAssessmentInProgress, setIsAssessmentInProgress] = useState(false);
  const [assessmentResults, setAssessmentResults] = useState(null);
  
  // Clean up any assessment prompts when component unmounts
  useEffect(() => {
    return () => {
      const prompt = document.getElementById('assessment-prompt-overlay');
      if (prompt && prompt.parentNode) {
        prompt.parentNode.removeChild(prompt);
      }
    };
  }, []);

  const createAssessmentPromptElement = (goalDesc, createdGoalObj) => {
    // Remove any existing prompt elements
    const existingPrompt = document.getElementById('assessment-prompt-overlay');
    if (existingPrompt) {
      document.body.removeChild(existingPrompt);
    }
    
    // Create overlay
    const overlay = document.createElement('div');
    overlay.id = 'assessment-prompt-overlay';
    overlay.style.position = 'fixed';
    overlay.style.top = '0';
    overlay.style.left = '0';
    overlay.style.right = '0';
    overlay.style.bottom = '0';
    overlay.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
    overlay.style.display = 'flex';
    overlay.style.justifyContent = 'center';
    overlay.style.alignItems = 'center';
    overlay.style.zIndex = '9999';
    
    // Create modal
    const modal = document.createElement('div');
    modal.className = 'goal-modal';
    modal.style.backgroundColor = '#111725';
    modal.style.borderRadius = '8px';
    modal.style.width = '90%';
    modal.style.maxWidth = '500px';
    modal.style.boxShadow = '0 5px 20px rgba(0, 0, 0, 0.3)';
    
    // Create header
    const header = document.createElement('div');
    header.className = 'goal-modal-header';
    header.style.padding = '15px 20px';
    header.style.borderBottom = '1px solid rgba(255, 255, 255, 0.1)';
    header.style.display = 'flex';
    header.style.justifyContent = 'space-between';
    header.style.alignItems = 'center';
    
    const title = document.createElement('h2');
    title.textContent = 'Knowledge Assessment';
    title.style.margin = '0';
    title.style.color = '#c8746d';
    title.style.fontSize = '20px';
    
    const closeBtn = document.createElement('button');
    closeBtn.innerHTML = '&times;';
    closeBtn.style.background = 'none';
    closeBtn.style.border = 'none';
    closeBtn.style.color = '#7c7c7c';
    closeBtn.style.fontSize = '24px';
    closeBtn.style.cursor = 'pointer';
    closeBtn.onclick = () => {
      document.body.removeChild(overlay);
      if (onGoalCreated) onGoalCreated(createdGoalObj);
    };
    
    header.appendChild(title);
    header.appendChild(closeBtn);
    
    // Create content
    const content = document.createElement('div');
    content.className = 'goal-modal-content';
    content.style.padding = '30px';
    
    const heading = document.createElement('h3');
    heading.textContent = 'Take a quick knowledge assessment?';
    heading.style.fontSize = '18px';
    heading.style.marginTop = '0';
    heading.style.marginBottom = '15px';
    heading.style.color = '#e4e6eb';
    
    const desc = document.createElement('p');
    desc.innerHTML = `This will help us understand your current knowledge level and create a more personalized learning plan for <strong>${goalDesc}</strong>.`;
    desc.style.color = '#b0b3b8';
    desc.style.marginBottom = '25px';
    desc.style.lineHeight = '1.5';
    
    const btnContainer = document.createElement('div');
    btnContainer.style.display = 'flex';
    btnContainer.style.gap = '15px';
    
    const skipBtn = document.createElement('button');
    skipBtn.textContent = 'Skip Assessment';
    skipBtn.style.flex = '1';
    skipBtn.style.padding = '10px';
    skipBtn.style.border = '1px solid rgba(255, 255, 255, 0.2)';
    skipBtn.style.borderRadius = '4px';
    skipBtn.style.background = 'rgba(255, 255, 255, 0.1)';
    skipBtn.style.color = '#e4e6eb';
    skipBtn.style.cursor = 'pointer';
    skipBtn.style.fontWeight = '500';
    skipBtn.onclick = () => {
      document.body.removeChild(overlay);
      if (onGoalCreated) onGoalCreated(createdGoalObj);
    };
    
    const takeBtn = document.createElement('button');
    takeBtn.textContent = 'Take Assessment';
    takeBtn.style.flex = '1';
    takeBtn.style.padding = '10px';
    takeBtn.style.border = 'none';
    takeBtn.style.borderRadius = '4px';
    takeBtn.style.background = '#c8746d';
    takeBtn.style.color = 'white';
    takeBtn.style.cursor = 'pointer';
    takeBtn.style.fontWeight = '500';
    
    // Store loading state in the button dataset
    takeBtn.dataset.loading = 'false';
    
    takeBtn.onclick = async () => {
      // Prevent multiple clicks
      if (takeBtn.dataset.loading === 'true') return;
      
      // Set loading state
      takeBtn.dataset.loading = 'true';
      takeBtn.textContent = 'Generating...';
      skipBtn.disabled = true;
      
      // Add spinner
      const spinner = document.createElement('div');
      spinner.className = 'spinner';
      spinner.style.display = 'inline-block';
      spinner.style.width = '20px';
      spinner.style.height = '20px';
      spinner.style.border = '3px solid rgba(255, 255, 255, 0.3)';
      spinner.style.borderRadius = '50%';
      spinner.style.borderTopColor = 'white';
      spinner.style.marginLeft = '10px';
      spinner.style.animation = 'spin 1s linear infinite';
      content.insertBefore(spinner, btnContainer);
      
      // Create keyframes for spinner animation
      const style = document.createElement('style');
      style.textContent = `
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `;
      document.head.appendChild(style);
      
      try {
        const assessment = await assessmentService.createKnowledgeAssessment();
        if (assessment && assessment.id) {
          // Remove the prompt and show assessment questions
          document.body.removeChild(overlay);
          startAssessment(assessment, createdGoalObj);
        } else {
          // Show error
          const errorMsg = document.createElement('div');
          errorMsg.style.color = '#ef5350';
          errorMsg.style.marginBottom = '15px';
          errorMsg.style.padding = '10px';
          errorMsg.style.backgroundColor = 'rgba(239, 83, 80, 0.1)';
          errorMsg.style.borderRadius = '4px';
          errorMsg.textContent = 'Failed to generate assessment. Please try again.';
          content.insertBefore(errorMsg, btnContainer);
          
          // Reset button
          takeBtn.dataset.loading = 'false';
          takeBtn.textContent = 'Take Assessment';
          skipBtn.disabled = false;
          content.removeChild(spinner);
        }
      } catch (err) {
        // Show error
        const errorMsg = document.createElement('div');
        errorMsg.style.color = '#ef5350';
        errorMsg.style.marginBottom = '15px';
        errorMsg.style.padding = '10px';
        errorMsg.style.backgroundColor = 'rgba(239, 83, 80, 0.1)';
        errorMsg.style.borderRadius = '4px';
        errorMsg.textContent = 'Failed to generate assessment. Please try again.';
        content.insertBefore(errorMsg, btnContainer);
        
        // Reset button
        takeBtn.dataset.loading = 'false';
        takeBtn.textContent = 'Take Assessment';
        skipBtn.disabled = false;
        if (spinner.parentNode === content) {
          content.removeChild(spinner);
        }
      }
    };
    
    btnContainer.appendChild(skipBtn);
    btnContainer.appendChild(takeBtn);
    
    content.appendChild(heading);
    content.appendChild(desc);
    content.appendChild(btnContainer);
    
    modal.appendChild(header);
    modal.appendChild(content);
    overlay.appendChild(modal);
    
    // Add to body
    document.body.appendChild(overlay);
  };

  const startAssessment = (assessment, createdGoal) => {
    setAssessmentId(assessment.id);
    setAssessmentQuestions(assessment.questions);
    setAnswers(new Array(assessment.questions.length).fill(null));
    setShowQuestions(true);
    setIsAssessmentInProgress(true);
    setCurrentQuestionIndex(0);
    setUserAnswers({});
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Convert skillLevel to metadata format expected by the API
      const metadata = JSON.stringify({ level: skillLevel });
      
      const newGoal = await goalService.createGoal(goalDescription, metadata);
      if (newGoal) {
        // Close the modal immediately
        onClose();
        
        // Wait a brief moment before showing the assessment prompt
        setTimeout(() => {
          createAssessmentPromptElement(goalDescription, newGoal);
        }, 100);
      }
    } catch (error) {
      console.error('Error creating goal:', error);
      setError('Failed to create goal. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleAnswerSelection = (optionIndex) => {
    const newAnswers = [...answers];
    newAnswers[currentQuestionIndex] = optionIndex;
    setAnswers(newAnswers);
  };
  
  const handleNextQuestion = () => {
    if (currentQuestionIndex < assessmentQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      handleSubmitAssessment();
    }
  };
  
  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };
  
  const handleSubmitAssessment = async () => {
    setIsSubmitting(true);
    setError('');
    
    try {
      // Format answers for the API
      const answersPayload = {
        answers: answers.map((answerIndex, questionIndex) => ({
          questionId: assessmentQuestions[questionIndex].id,
          selectedOptionIndex: answerIndex
        }))
      };
      
      const results = await assessmentService.submitKnowledgeAssessment(assessmentId, answersPayload);
      
      setIsSubmitting(false);
      setIsAssessmentComplete(true);
      setAssessmentResults(results);
      
      // Redirect to dashboard/roadmap after a short delay
      setTimeout(() => {
        setShowQuestions(false);
        window.location.href = '/dashboard';
      }, 2000);
      
    } catch (err) {
      console.error('Failed to submit assessment:', err);
      setError('Failed to submit assessment. Please try again.');
      setIsSubmitting(false);
    }
  };
  
  // Parse question text and options from the question format
  const parseQuestion = (questionText) => {
    if (!questionText) return { text: '', options: [] };
    
    const lines = questionText.split('\n');
    let questionLine = '';
    const options = [];
    let correctAnswer = '';
    
    lines.forEach(line => {
      if (line.startsWith('Q:')) {
        questionLine = line.substring(2).trim();
      } else if (line.startsWith('A:')) {
        options.push(line.substring(2).trim());
      } else if (line.startsWith('B:')) {
        options.push(line.substring(2).trim());
      } else if (line.startsWith('C:')) {
        options.push(line.substring(2).trim());
      } else if (line.startsWith('D:')) {
        options.push(line.substring(2).trim());
      } else if (line.startsWith('Correct:')) {
        correctAnswer = line.substring(8).trim();
      }
    });
    
    return { text: questionLine, options, correctAnswer };
  };

  const handleSelectAnswer = (questionId, selectedOption) => {
    setUserAnswers(prev => ({
      ...prev,
      [questionId]: selectedOption
    }));
  };

  const renderAssessmentQuestion = () => {
    if (!assessmentQuestions.length || currentQuestionIndex >= assessmentQuestions.length) return null;
    
    const currentQuestion = assessmentQuestions[currentQuestionIndex];
    const parsedQuestion = parseQuestion(currentQuestion);
    
    return (
      <div className="assessment-question">
        <h3>Question {currentQuestionIndex + 1} of {assessmentQuestions.length}</h3>
        <p className="question-text">{parsedQuestion.text}</p>
        <div className="options-container">
          {parsedQuestion.options.map((option, index) => (
            <div 
              key={index} 
              className={`option ${userAnswers[currentQuestion.id] === option ? 'selected' : ''}`}
              onClick={() => handleSelectAnswer(currentQuestion.id, option)}
            >
              <span className="option-label">{String.fromCharCode(65 + index)}.</span>
              <span className="option-text">{option}</span>
            </div>
          ))}
        </div>
        <div className="assessment-navigation">
          <button 
            type="button" 
            onClick={handlePreviousQuestion} 
            disabled={currentQuestionIndex === 0}
            className="secondary-button"
          >
            Previous
          </button>
          <button 
            type="button" 
            onClick={handleNextQuestion} 
            className="primary-button"
            disabled={!userAnswers[currentQuestion.id]}
          >
            {currentQuestionIndex === assessmentQuestions.length - 1 ? 'Submit' : 'Next'}
          </button>
        </div>
      </div>
    );
  };

  const renderAssessmentResults = () => {
    if (!assessmentResults) return null;
    
    return (
      <div className="assessment-results">
        <h3>Assessment Results</h3>
        <p>Score: {assessmentResults.score}%</p>
        <p>{assessmentResults.feedback}</p>
        <button 
          type="button" 
          onClick={onClose} 
          className="primary-button"
        >
          Continue to Dashboard
        </button>
      </div>
    );
  };

  // Show assessment questions one by one
  if (showQuestions) {
    // Show completion message
    if (isAssessmentComplete) {
      return (
        <div className="goal-modal-overlay" style={{ display: 'flex' }}>
          <div className="goal-modal">
            <div className="goal-modal-header">
              <h2>Assessment Complete</h2>
            </div>
            
            <div className="goal-modal-content generating">
              <h3>Thank you for completing the assessment!</h3>
              <p>Your learning plan is being customized based on your responses.</p>
              <div className="spinner"></div>
              <p>Redirecting to your roadmap...</p>
            </div>
          </div>
        </div>
      );
    }
    
    // Show current question
    if (assessmentQuestions.length > 0 && currentQuestionIndex < assessmentQuestions.length) {
      return (
        <div className="goal-modal-overlay" style={{ display: 'flex' }}>
          <div className="goal-modal">
            <div className="goal-modal-header">
              <h2>Knowledge Assessment</h2>
              <button className="close-button" onClick={() => onClose()}>&times;</button>
            </div>
            
            <div className="goal-modal-content">
              <div className="assessment-progress">
                Question {currentQuestionIndex + 1} of {assessmentQuestions.length}
              </div>
              
              <div className="question-container">
                {renderAssessmentQuestion()}
              </div>
            </div>
          </div>
        </div>
      );
    }
  }

  // Regular goal creation form
  return (
    <Modal isOpen={isOpen} onClose={onClose} className="goal-modal">
      <div className={`goal-modal-container ${isSubmitting ? 'loading' : ''}`}>
        {isSubmitting && (
          <>
            <div className="loading-spinner"><div></div><div></div><div></div><div></div></div>
          </>
        )}

        <form onSubmit={handleSubmit}>
          {previewImage && (
            <div className="goal-image-preview">
              <img src={previewImage} alt="Goal preview" />
              <p className="preview-note">Image will be generated automatically</p>
            </div>
          )}
          
          <div className="form-group">
            <label htmlFor="goal-description">What do you want to learn?</label>
            <textarea
              id="goal-description"
              value={goalDescription}
              onChange={(e) => setGoalDescription(e.target.value)}
              placeholder="e.g., 'Learn React hooks and functional components' or 'Master SQL database design'"
              required
            />
            <small>Be specific to get better recommendations</small>
          </div>
          
          <div className="form-group">
            <label htmlFor="skill-level">Your desired course difficulty</label>
            <select
              id="skill-level"
              value={skillLevel}
              onChange={(e) => setSkillLevel(e.target.value)}
            >
              <option value="Beginner">Beginner</option>
              <option value="Practitioner">Practitioner</option>
              <option value="Advanced">Advanced</option>
            </select>
            <small>This helps tailor the learning plan to your needs</small>
          </div>
          
          {error && <div className="error-message">{error}</div>}
          
          <div className="button-row">
            <button 
              type="button" 
              className="cancel-button" 
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="submit-button"
              disabled={isSubmitting || !goalDescription.trim()}
            >
              {isSubmitting ? 'Creating...' : 'Create Goal'}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default GoalModal; 